import { useEffect, useRef, useState } from "react";
import { BOARDS } from "@/constants/assets";

/**
 * Niche-relevant 3D hero:
 * A floating collage of REAL ThemeStudios portfolio boards drifting in space
 * with mouse-reactive parallax. Communicates "we make posters, reels, branding,
 * monthly systems" the moment the hero loads — no abstract chrome.
 */

// Each tile gets its own depth (z), base position, drift speed, and tilt.
// Z is used for parallax intensity AND scale (deeper = smaller + slower shift).
const TILES = [
  // Center hero board — the master 2026 catalog
  { img: BOARDS.master, x: 4, y: -2, z: 1.0, w: 380, rot: -2, speed: 1.0, delay: 0 },
  // Top-left
  { img: BOARDS.postersSocial, x: -30, y: -22, z: 0.6, w: 260, rot: -8, speed: 1.6, delay: 0.4 },
  // Bottom-left
  { img: BOARDS.reelsBranding, x: -24, y: 26, z: 0.72, w: 280, rot: 6, speed: 1.3, delay: 0.6 },
  // Top-right
  { img: BOARDS.adsProfile, x: 34, y: -22, z: 0.62, w: 260, rot: 7, speed: 1.5, delay: 0.8 },
  // Bottom-right
  { img: BOARDS.monthlyUniversal, x: 32, y: 24, z: 0.78, w: 280, rot: -5, speed: 1.2, delay: 1.0 },
  // Far back left
  { img: BOARDS.clinicStartup, x: -46, y: 4, z: 0.38, w: 190, rot: -12, speed: 2.0, delay: 1.2 },
  // Far back right
  { img: BOARDS.restaurantSalon, x: 46, y: -2, z: 0.35, w: 190, rot: 10, speed: 2.2, delay: 1.4 },
  // Far back bottom
  { img: BOARDS.constructionRetail, x: 10, y: 40, z: 0.33, w: 180, rot: 4, speed: 2.4, delay: 1.6 },
];

export default function HeroScene() {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const tileRefs = useRef([]);
  const [ready, setReady] = useState(false);

  // Mouse parallax tracker
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      mouse.current.tx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.ty = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  // Background dust + tile parallax + per-tile drift
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = (canvas.width = canvas.offsetWidth * window.devicePixelRatio);
    let h = (canvas.height = canvas.offsetHeight * window.devicePixelRatio);
    const onResize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    window.addEventListener("resize", onResize);

    const N = 70;
    const particles = Array.from({ length: N }).map(() => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.4 + 0.2,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      a: Math.random() * 0.45 + 0.1,
    }));

    setReady(true);
    const startTime = performance.now();

    const tick = () => {
      const t = (performance.now() - startTime) / 1000;

      // Ease mouse
      mouse.current.x += (mouse.current.tx - mouse.current.x) * 0.06;
      mouse.current.y += (mouse.current.ty - mouse.current.y) * 0.06;

      // Per-tile parallax + drift
      TILES.forEach((tile, i) => {
        const node = tileRefs.current[i];
        if (!node) return;
        const parallaxX = -mouse.current.x * 28 * tile.z;
        const parallaxY = -mouse.current.y * 28 * tile.z;
        // Float drift — each tile bobs on its own sine wave
        const drift = Math.sin(t * tile.speed + tile.delay * 2) * 6;
        node.style.transform = `translate3d(${parallaxX}px, ${parallaxY + drift}px, 0) rotate(${tile.rot}deg) scale(1)`;
      });

      // Background dust
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        const px = p.x + mouse.current.x * 10;
        const py = p.y + mouse.current.y * 10;
        ctx.beginPath();
        ctx.arc(px, py, p.r * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${p.a})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid="hero-3d-canvas"
      className="absolute inset-0 overflow-hidden"
      style={{ opacity: ready ? 1 : 0, transition: "opacity 800ms ease" }}
    >
      {/* Cinematic gradient haze */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 70% 55%, rgba(224,60,49,0.22) 0%, rgba(224,60,49,0.06) 30%, transparent 65%), radial-gradient(ellipse at 20% 30%, rgba(255,255,255,0.04) 0%, transparent 50%)",
        }}
      />

      {/* Red light bloom behind the centerpiece */}
      <div className="hero-bloom" />

      {/* Stage with the floating boards */}
      <div className="hero-stage" aria-hidden="true">
        {TILES.map((tile, i) => (
          <div
            key={i}
            ref={(el) => (tileRefs.current[i] = el)}
            className="hero-tile"
            style={{
              left: `calc(50% + ${tile.x}%)`,
              top: `calc(50% + ${tile.y}%)`,
              width: `${tile.w}px`,
              transform: `rotate(${tile.rot}deg)`,
              zIndex: Math.round(tile.z * 10),
              opacity: Math.max(0.55, tile.z),
              filter: `blur(${(1 - tile.z) * 2.5}px) brightness(${0.55 + tile.z * 0.45})`,
              animation: `tile-in 1100ms cubic-bezier(0.22, 1, 0.36, 1) ${tile.delay}s both`,
            }}
          >
            <img
              src={tile.img}
              alt=""
              draggable={false}
              loading="eager"
              className="w-full h-auto rounded-md select-none pointer-events-none"
              style={{
                boxShadow: `0 ${20 * tile.z}px ${50 * tile.z}px rgba(0,0,0,${0.45 + tile.z * 0.35}),
                            0 0 0 1px rgba(255,255,255,0.04)`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Particle dust */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ width: "100%", height: "100%" }}
      />

      {/* Dark overlay so headline stays readable on top — softer than before so boards remain visible */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent pointer-events-none" />

      {/* Bottom fade into next section */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />

      <style>{`
        .hero-stage {
          position: absolute;
          inset: 0;
          perspective: 1400px;
          transform-style: preserve-3d;
        }
        .hero-tile {
          position: absolute;
          transform-origin: center center;
          will-change: transform, opacity;
          translate: -50% -50%;
        }
        .hero-bloom {
          position: absolute;
          left: 65%;
          top: 50%;
          width: 760px;
          height: 760px;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle, rgba(224,60,49,0.55) 0%, rgba(224,60,49,0.12) 30%, transparent 65%);
          filter: blur(60px);
          animation: bloom-pulse 7s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes bloom-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.85; }
          50%      { transform: translate(-50%, -50%) scale(1.08); opacity: 1; }
        }
        @keyframes tile-in {
          0%   { opacity: 0; transform: translate3d(0, 30px, 0) rotate(0deg) scale(0.92); filter: blur(8px); }
          100% { opacity: var(--tile-opacity, 0.9); filter: blur(var(--tile-blur, 0px)); }
        }
        @media (max-width: 900px) {
          .hero-tile { transform: scale(0.7); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-bloom { animation: none; }
          .hero-tile { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
